// Task ownership (owner decision 2026-10-06): "a task I created is a task I own".
// Every employee can add more assignees to a task from a dropdown, but the
// creator is the owner (`assigned_agent_id`). Pure functions only (no React), so
// the mapping is unit-tested in src/utils/__tests__/taskAssigneesModel.test.ts.
//
// The payload shape is unchanged: `assigned_agent_id` (one id or undefined) and
// `additional_agent_ids` (ids or undefined), exactly as the forms sent before.

/** One row of `GET /tickets/meta/assignable-agents`. */
export interface AssignableAgent {
  id: string;
  name: string;
  email?: string;
  role?: string;
  is_account_manager?: boolean;
}

export const isStaffRole = (role?: string | null) => role === 'admin' || role === 'agent';

/**
 * The creator as the task's fixed owner: only when the signed-in user is staff
 * and appears in the assignable agents for the chosen organization. Otherwise
 * `null`, and the form lets the user pick the owner instead.
 */
export function creatorAsOwner(
  user: { id: string; role?: string | null } | null | undefined,
  agents: readonly AssignableAgent[],
): AssignableAgent | null {
  if (!user || !isStaffRole(user.role)) return null;
  return agents.find((a) => a.id === user.id) ?? null;
}

export interface TaskAssigneesInput {
  /** The creator, when they are the fixed owner (see `creatorAsOwner`). */
  creatorOwnerId?: string | null;
  /** The owner picked by hand (fallback when the creator is not assignable). */
  pickedOwnerId?: string | null;
  /** People chosen in "משויכים נוספים", in the order they were added. */
  additionalIds: readonly string[];
}

export interface TaskAssigneesPayload {
  assigned_agent_id: string | undefined;
  additional_agent_ids: string[] | undefined;
}

/**
 * Owner and additional assignees for `api.createTicket`.
 * - Owner: the creator when assignable, else the picked owner, else the first
 *   additional person (the old "first selected is the owner" rule, so creation
 *   never breaks).
 * - Additional: de-duplicated, never the owner, order kept.
 */
export function resolveTaskAssignees({ creatorOwnerId, pickedOwnerId, additionalIds }: TaskAssigneesInput): TaskAssigneesPayload {
  const unique = Array.from(new Set(additionalIds.filter(Boolean)));
  const owner = creatorOwnerId || pickedOwnerId || unique[0] || undefined;
  const additional = unique.filter((id) => id !== owner);
  return {
    assigned_agent_id: owner,
    additional_agent_ids: additional.length > 0 ? additional : undefined,
  };
}

/** Options for the "משויכים נוספים" dropdown: everyone but the owner, filtered by a query. */
export function additionalOptions(
  agents: readonly AssignableAgent[],
  ownerId: string | null | undefined,
  query = '',
): AssignableAgent[] {
  const q = query.trim().toLocaleLowerCase();
  return agents.filter((a) => a.id !== ownerId && (!q
    || a.name.toLocaleLowerCase().includes(q)
    || (a.email ?? '').toLocaleLowerCase().includes(q)));
}
